import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import SpriteText from 'three-spritetext';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import GameStore from '../shared/GameStore';
import { get } from 'svelte/store';
import { addKeyEvents, runController, setControllerProperties, } from './utils/Controller';
import ThirdPersonCamera from './utils/ThirdPersonCamera';
class Game {
    constructor(scene, world, loader, material, camera, width, playerTextureName, skyboxFolderName, skyboxExtension, useOrbitCamera) {
        this.scene = scene;
        this.world = world;
        this.loader = loader;
        this.material = material;
        this.camera = camera;
        this.width = width;
        this.playerTextureName = playerTextureName;
        this.skyboxFolderName = skyboxFolderName;
        this.skyboxExtension = skyboxExtension;
        this.useOrbitCamera = useOrbitCamera;
        this.activeGamePieces = [];
        this.movingPieces = [];
        // Used to move a gamepiece
        this.move = (gamePiece, estimatedTime) => {
            if (!gamePiece.movementType) {
                gamePiece.mesh.position.copy(gamePiece.body.position);
                gamePiece.mesh.quaternion.copy(gamePiece.body.quaternion);
            }
            else {
                const { start, distance, speed, positionOffset, direction } = gamePiece.movementType;
                const movement = start === 'sin'
                    ? Math.sin(estimatedTime * speed) * distance + positionOffset
                    : Math.cos(Math.PI / 2 + estimatedTime * speed) * distance +
                        positionOffset;
                switch (direction) {
                    case 'x':
                        gamePiece.mesh.position.x = movement;
                        gamePiece.body.position.x = movement;
                        break;
                    case 'y':
                        gamePiece.mesh.position.y = movement;
                        gamePiece.body.position.y = movement;
                        break;
                    case 'z':
                        gamePiece.mesh.position.z = movement;
                        gamePiece.body.position.z = movement;
                        break;
                }
            }
        };
        this.rotate = (gamePiece, estimatedTime) => {
            if (!gamePiece.movementType) {
                gamePiece.mesh.position.copy(gamePiece.body.position);
                gamePiece.mesh.quaternion.copy(gamePiece.body.quaternion);
            }
            else {
                const { distance, speed, positionOffset, direction } = gamePiece.movementType;
                const movement = Math.sin(estimatedTime * speed) * distance + positionOffset;
                switch (direction) {
                    case 'x':
                        gamePiece.mesh.rotation.x = movement;
                        break;
                    case 'y':
                        gamePiece.mesh.rotation.y = movement;
                        break;
                    case 'z':
                        gamePiece.mesh.rotation.z = movement;
                        break;
                }
                gamePiece.body.quaternion.copy(gamePiece.mesh.quaternion);
            }
        };
        this.scene = scene;
        this.world = world;
        this.loader = loader;
        this.material = material;
        if (this.useOrbitCamera) {
            this.orbitCamera = new OrbitControls(this.camera, document.querySelector('canvas'));
        }
        else {
            this.gameCamera = new ThirdPersonCamera(camera);
        }
        this.gamePieceTexture = this.loader
            .getTextureLoader()
            .load(`textures/playerTextures/${playerTextureName}`);
        this.createSkybox(skyboxFolderName, skyboxExtension);
        addKeyEvents();
    }
    createSkybox(path, extension) {
        const cubeLoader = this.loader.getCubeTextureLoader();
        const texture = cubeLoader.load([
            `textures/skybox/${path}/px${extension}`,
            `textures/skybox/${path}/nx${extension}`,
            `textures/skybox/${path}/py${extension}`,
            `textures/skybox/${path}/ny${extension}`,
            `textures/skybox/${path}/pz${extension}`,
            `textures/skybox/${path}/nz${extension}`,
        ]);
        this.scene.background = texture;
    }
    createPlayer() {
        const startPosition = {
            x: (0.5 - Math.random()) * this.width,
            y: Math.random() * (this.width / 2) + 10,
            z: (0.5 - Math.random()) * this.width,
        };
        const mesh = new THREE.Mesh(new THREE.SphereBufferGeometry(5, 64, 64), new THREE.MeshStandardMaterial({ map: this.gamePieceTexture }));
        mesh.castShadow = true;
        mesh.position.copy(startPosition);
        mesh.name = get(GameStore).username;
        // Create the physics object to match the mesh object
        const boxShape = new CANNON.Sphere(5);
        const body = new CANNON.Body({
            mass: 1,
            position: new CANNON.Vec3(5, 160, 0),
            shape: boxShape,
            material: this.material.getIceMaterial(),
        });
        body.position.copy(startPosition);
        // Add entities to the world
        this.scene.add(mesh);
        this.world.addBody(body);
        this.currentGamePiece = { mesh, body };
        this.spriteText = new SpriteText(this.currentGamePiece.mesh.name);
        this.spriteText.scale.set(22.5, 5, 0);
        this.scene.add(this.spriteText);
        // Creates the controller for the object
        setControllerProperties(this.currentGamePiece, this.gameCamera);
        this.activeGamePieces.push(this.currentGamePiece);
        if (!this.useOrbitCamera)
            this.gameCamera.setTracking(this.currentGamePiece);
    }
    addToWorld(gamePiece) {
        this.scene.add(gamePiece.mesh);
        this.world.addBody(gamePiece.body);
        if (gamePiece.movementType)
            this.activeGamePieces.push(gamePiece);
    }
    respawnIfDead(limit = -50) {
        if (this.currentGamePiece.mesh.position.y <= limit) {
            this.currentGamePiece.body.position.set((0.5 - Math.random()) * this.width, 150, (0.5 - Math.random()) * this.width);
            this.currentGamePiece.body.angularVelocity.set(0, 0, 0);
            this.currentGamePiece.body.velocity.set(0, 0, 0);
        }
    }
    runGameUpdates(timeDelta, elapsedTime, respawnOffset) {
        const { x, y, z } = this.currentGamePiece.mesh.position;
        this.respawnIfDead(respawnOffset);
        runController();
        for (const gamePiece of this.activeGamePieces) {
            this.move(gamePiece, elapsedTime);
        }
        for (const testObj of this.movingPieces) {
            this.rotate(testObj, elapsedTime);
        }
        this.spriteText.position.set(x, y + 14, z);
        this.world.step(1 / 100, timeDelta);
    }
    // Creates the physical plane boundry of a Plane
    createBoundry(x1, y1, z1, x2, y2, z2, rotation, floorShape) {
        const body = new CANNON.Body({
            mass: 0,
            shape: floorShape,
            material: this.material.getRockMaterial(),
        });
        body.quaternion.setFromAxisAngle(new CANNON.Vec3(x1, y1, z1), rotation);
        body.position = new CANNON.Vec3(x2, y2, z2);
        this.world.addBody(body);
    }
    // Used for keeping score of how long a game have been running
    updatePlaytime(elapsedTime) {
        GameStore.update((store) => {
            return Object.assign(Object.assign({}, store), { elapsedTime });
        });
    }
}
export default Game;
